import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Subcontractor } from '../../../types';
import { KlSheet } from '../modal/KlSheet';

interface MobileContractorListSheetProps {
    open: boolean;
    onClose: () => void;
    subcontractors: Subcontractor[];
    onEditSubcontractor: (sub: Subcontractor) => void;
}

export const MobileContractorListSheet: React.FC<MobileContractorListSheetProps> = ({
    open,
    onClose,
    subcontractors,
    onEditSubcontractor
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Cerrar menú contextual al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setActiveMenuId(null);
            }
        };
        if (activeMenuId) {
            document.addEventListener('pointerdown', handleClickOutside);
            return () => document.removeEventListener('pointerdown', handleClickOutside);
        }
    }, [activeMenuId]);

    const filteredSubcontractors = useMemo(() => {
        if (!searchTerm.trim()) return subcontractors;
        const q = searchTerm.toLowerCase().trim();
        return subcontractors.filter(s =>
            (s.razonSocial && s.razonSocial.toLowerCase().includes(q)) ||
            (s.rut && s.rut.toLowerCase().includes(q))
        );
    }, [subcontractors, searchTerm]);

    const showSearch = subcontractors.length > 8;

    return (
        <KlSheet
            open={open}
            onClose={onClose}
            top={56}
            title="Lista de Empresas Contratistas"
            footer={
                <button
                    type="button"
                    onClick={onClose}
                    className="b b-o w-full"
                >
                    Cerrar
                </button>
            }
        >
            <div className="flex flex-col gap-3 p-3">
                {/* Buscador de 40px si hay más de 8 empresas */}
                {showSearch && (
                    <div className="h-10 border border-[#E2E8F0] dark:border-slate-700 rounded-[10px] bg-white dark:bg-slate-800 px-3 flex items-center gap-2 shrink-0">
                        <svg
                            className="w-[18px] h-[18px] text-slate-400 shrink-0"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                        </svg>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar por R.U.T o razón social…"
                            className="w-full bg-transparent text-[13px] text-[#0F172A] dark:text-slate-100 placeholder:text-[#94A3B8] focus:outline-none"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => setSearchTerm('')}
                                className="text-slate-400 hover:text-slate-600 p-1"
                            >
                                <span className="material-symbols-outlined text-[16px]">close</span>
                            </button>
                        )}
                    </div>
                )}

                {/* Lista de Empresas Contratistas */}
                {filteredSubcontractors.length === 0 ? (
                    <div className="p-8 text-center text-xs text-slate-400 bg-white dark:bg-slate-800/50 rounded-[14px] border border-dashed border-[#E2E8F0] dark:border-slate-700">
                        {searchTerm ? 'No se encontraron empresas coincidentes.' : 'No hay empresas contratistas registradas.'}
                    </div>
                ) : (
                    <div className="flex flex-col gap-2.5">
                        {filteredSubcontractors.map((sub) => {
                            const isActivo = (sub.status || 'Activo') === 'Activo';
                            const isMenuOpen = activeMenuId === sub.id;

                            return (
                                <div
                                    key={sub.id}
                                    className="card relative flex-row items-center justify-between gap-3 bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700"
                                >
                                    <div className="flex-1 min-w-0 pr-1">
                                        <h4
                                            className="text-[14px] font-bold text-[#0F172A] dark:text-slate-100 leading-snug line-clamp-2"
                                            title={sub.razonSocial}
                                        >
                                            {sub.razonSocial}
                                        </h4>
                                        <div className="font-mono text-[12px] text-[#64748B] dark:text-slate-400 mt-1">
                                            {sub.rut || 'S/RUT'}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                        {/* Insignia de estado */}
                                        <span
                                            className={`text-[11px] font-bold px-2.5 py-1 rounded-[10px] ${
                                                isActivo
                                                    ? 'bg-[#DCFCE7] text-[#166534] dark:bg-green-950/40 dark:text-green-300'
                                                    : 'bg-[#FEE2E2] text-[#B91C1C] dark:bg-red-950/40 dark:text-red-300'
                                            }`}
                                        >
                                            {sub.status || 'Activo'}
                                        </span>

                                        {/* Menú ⋮ de acciones */}
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveMenuId(prev => (prev === sub.id ? null : sub.id));
                                                }}
                                                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                                aria-label="Acciones de empresa"
                                            >
                                                <span className="material-symbols-outlined text-[20px]">more_vert</span>
                                            </button>

                                            {isMenuOpen && (
                                                <div
                                                    ref={menuRef}
                                                    className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-30 animate-in fade-in zoom-in duration-100"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveMenuId(null);
                                                            onEditSubcontractor(sub);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2 cursor-pointer"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px] text-blue-600 dark:text-blue-400">edit</span>
                                                        <span>Editar</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </KlSheet>
    );
};
